import { Fragment, type ReactNode } from 'react';

/** Renders one tag's inner text, e.g. `children => <a href="...">{children}</a>`. */
export type TagRenderer = (children: string) => ReactNode;

/** `<1>text</1>`: a numbered pair with the same number on both ends. */
const TAG_PAIR = /<(\d+)>(.*?)<\/\1>/gs;

/** Any leftover tag a translation mangled or left unpaired (`<1>`, `</ 1>`, `<1/>`). */
const STRAY_TAG = /<\s*\/?\s*\d+\s*\/?\s*>/g;

/**
 * A translated sentence with inline elements, from one catalogue string with numbered tags:
 *
 *     renderTagged(t('landing.resourceHub.body'), { 1: children => <a href={URL}>{children}</a> })
 *
 * for `"Explore our Insights page on <1>Britebound.org</1> for more ways..."`. Keeping the sentence
 * whole lets machine translation reorder it; splitting it into fragments around the link does not.
 *
 * Pure and hook-free, so server and client components can both call it (react-i18next's `Trans`
 * needs the i18next context). A tag a translation dropped or mangled renders as plain text, and a
 * tag with no renderer renders its inner text, so a bad translation loses the link, never the copy.
 */
export function renderTagged(text: string, tags: Record<number, TagRenderer>): ReactNode[] {
    const nodes: ReactNode[] = [];
    const pushText = (value: string) => {
        const clean = value.replace(STRAY_TAG, '');
        if (!clean) return;
        const previous = nodes.length - 1;
        if (typeof nodes[previous] === 'string') nodes[previous] += clean;
        else nodes.push(clean);
    };

    let rest = 0;
    for (const match of text.matchAll(TAG_PAIR)) {
        const [whole, id, inner] = match;
        pushText(text.slice(rest, match.index));
        const render = tags[Number(id)];
        if (render) nodes.push(<Fragment key={nodes.length}>{render(inner.replace(STRAY_TAG, ''))}</Fragment>);
        else pushText(inner);
        rest = match.index + whole.length;
    }
    pushText(text.slice(rest));
    return nodes;
}
